import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  useMediaQuery,
  useTheme,
  Grid,
  Stack,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import dayjs from 'dayjs';

type CarCardProps = {
  vehicle?: any;
  viewMode?: 'grid' | 'list';
};

const CarCard = ({ vehicle, viewMode = 'grid' }: CarCardProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const effectiveViewMode = isMobile ? 'grid' : viewMode;
  const isList = effectiveViewMode === 'list';

  const formatPrice = (price: number) =>
    new Intl.NumberFormat('en-SG', {
      style: 'currency',
      currency: 'SGD',
      minimumFractionDigits: 0,
    }).format(price);

  const formatDate = (date?: string) =>
    date ? dayjs(date).format('DD/MM/YYYY') : '';

  const VehicleViewCountTag = ({ count }: { count: number }) => (
    <Box
      sx={{
        position: 'absolute',
        top: 8,
        left: 8,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        color: '#fff',
        px: 1,
        py: 0.25,
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        fontSize: 12,
        zIndex: 1,
      }}
    >
      <VisibilityIcon sx={{ fontSize: 14 }} />
      {count.toLocaleString()}
    </Box>
  );

  const VehicleSoldTag = () => (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        right: 0,
        bgcolor: 'warning.main',
        color: 'black',
        px: 1.5,
        py: 0.5,
        fontWeight: 'bold',
        fontSize: 12,
        borderTopRightRadius: 8,
        borderBottomLeftRadius: 8,
        zIndex: 2,
      }}
    >
      SOLD
    </Box>
  );

  if (!vehicle) return null;

  return (
    <Card
      component={RouterLink}
      to={`/vehicle-details/${vehicle.vehicleId}`}
      sx={{
        display: 'flex',
        flexDirection: isList ? 'row' : 'column',
        width: '100%',
        height: isList ? 200 : '100%',
        borderRadius: 2,
        border: '1px solid #e0e0e0',
        boxShadow: 2,
        position: 'relative',
        textDecoration: 'none',
        '&:hover': { boxShadow: 4 },
        overflow: 'hidden',
        color: 'inherit',
      }}
    >
      {vehicle?.statusId === 'VSL' && <VehicleSoldTag />}

      {/* Image Section */}
      <Box
        sx={{
          position: 'relative',
          width: isList ? 210 : '100%',
          aspectRatio: '4 / 3',
          backgroundColor: '#f5f5f5',
          flexShrink: 0,
          overflow: 'hidden',
        }}
      >
        <CardMedia
          component="img"
          image={
            vehicle?.vehicleImageUrl ||
            (isList
              ? 'https://via.placeholder.com/300x180?text=No+Image'
              : 'https://via.placeholder.com/1024x768?text=No+Image')
          }
          alt={vehicle?.vehicleModel || 'Vehicle Image'}
          loading="lazy"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
          }}
        />

        {/* Premium Ad Tag */}
        {vehicle?.racingAd && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'warning.main',
              color: 'warning.contrastText',
              fontWeight: 'bold',
              fontSize: '0.75rem',
              px: 1,
              py: 0.25,
              borderRadius: 1,
              boxShadow: 1,
            }}
          >
            Racing Ad
          </Box>
        )}

        {/* View Count Tag */}
        {vehicle?.viewCount != null && (
          <VehicleViewCountTag count={vehicle.viewCount} />
        )}
      </Box>

      {/* Content Section */}
      <CardContent sx={{ flex: 1, p: 2 }}>
        <Stack spacing={1}>
          {/* Grid Mode */}
          {!isList && (
            <>
              {/* Price */}
              <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                {formatPrice(vehicle.askingPrice)}
              </Typography>

              {/* Brand & Model Name - Increased Size & Boldness */}
              <Grid size={{ xs: 12, md: 12 }}>
                <Typography
                  variant="h6" // Changed from body2 to h6 for larger text
                  fontWeight="bold" // Ensures it is bold
                  color="text.primary" // Ensures text uses theme primary color (black/dark)
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    lineHeight: 1.2,
                  }}
                >
                  {vehicle.vehicleBrand} {vehicle.vehicleModelName}
                </Typography>
              </Grid>

              {/* Secondary Details */}
              <Typography variant="caption" color="text.primary"> {/* Changed from text.secondary to primary if you want strictly black/stronger text */}
                {[
                  vehicle.firstRegistrationDate && formatDate(vehicle.firstRegistrationDate),
                  vehicle.mileage && `${vehicle.mileage.toLocaleString()} km`,
                  vehicle.vehicleBodyType,
                ]
                  .filter(Boolean)
                  .join(' · ') || '-'}
              </Typography>

              {/* Dealer Info */}
              {vehicle?.showDealerInVehicleList && (
                <Typography
                  variant="caption"
                  color="#70287f"
                  fontWeight={600}
                  sx={{ mt: 0.5 }}
                >
                  {vehicle.dealerName}
                </Typography>
              )}
            </>
          )}

          {/* List Mode */}
          {isList && (
            <>
              {/* Title & Price Header */}
              <Grid container spacing={1}>
                <Grid size={{ xs: 12, md: 8 }}>
                  {/* Brand & Model Name - Increased Size & Boldness */}
                  <Typography
                    variant="h6" // Changed from body2 to h6
                    fontWeight="bold"
                    color="text.primary"
                    noWrap
                  >
                    {vehicle.vehicleBrand} {vehicle.vehicleModelName}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }} textAlign={{ xs: 'left', md: 'right' }}>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    {formatPrice(vehicle.askingPrice)}
                  </Typography>
                </Grid>
              </Grid>

              {/* Vehicle Details */}
              <Grid container spacing={1} mt={1} alignItems="center">
                {[
                  {
                    label: 'Registration',
                    value: vehicle.firstRegistrationDate && formatDate(vehicle.firstRegistrationDate),
                  },
                  { label: 'Fuel', value: vehicle.vehicleFuelType },
                  { label: 'Transmission', value: vehicle.vehicleTransmissionType },
                  { label: 'Body Type', value: vehicle.vehicleBodyType },
                  { label: 'Class', value: vehicle.vehicleClass },
                  {
                    label: 'Depreciation',
                    value:
                      vehicle.depreciationValue !== null && vehicle.depreciationValue !== undefined
                        ? `$${vehicle.depreciationValue.toLocaleString()} / year`
                        : null,
                  },
                  {
                    label: 'Mileage',
                    value:
                      vehicle.mileage !== null && vehicle.mileage !== undefined
                        ? `${vehicle.mileage.toLocaleString()} km`
                        : null,
                  },
                ]
                  .slice(0, 10)
                  .map(({ label, value }) => (
                    <Grid size={{ xs: 6, md: 2.4 }} key={label}>
                      <Typography variant="caption" color="text.primary" noWrap> {/* Changed to text.primary for blacker text */}
                        <strong>{label}:</strong> {value || '-'}
                      </Typography>
                    </Grid>
                  ))}
              </Grid>

              {/* Description */}
              {vehicle.description && (
                <Typography
                  variant="caption"
                  color="text.primary" // Stronger text color
                  mt={1}
                  sx={{
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    '& p': { margin: 0 },
                  }}
                  component="div"
                >
                  {new DOMParser().parseFromString(vehicle.description, 'text/html').body.textContent || ''}
                </Typography>
              )}

              {/* Dealer Info */}
              {vehicle?.showDealerInVehicleList && (
                <Typography
                  variant="caption"
                  color="text.primary"
                  mt={1}
                  sx={{ fontWeight: 600 }}
                >
                  {vehicle.dealerName} | Posted:{' '}
                  {new Date(vehicle?.addDate).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })}
                </Typography>
              )}
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default CarCard;